/**
 * Login.tsx — Sign-in page.
 *
 * Rebuilt in Pass 7 to match the new landing aesthetic.
 * Preserves: Auth0 SSO, email/password fallback, AdminContext wiring,
 * session storage of meridian_token, navigation to /dashboard on success.
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import { useAdmin } from '../../AdminContext';

const C = {
  bgBase: '#080C18',
  bgPanel: '#0B0F1F',
  bgCard: '#0F1628',
  bgInput: '#0F1628',
  borderSubtle: 'rgba(148, 163, 184, 0.08)',
  borderDefault: 'rgba(148, 163, 184, 0.12)',
  borderStrong: 'rgba(148, 163, 184, 0.20)',
  textPrimary: '#E8EFF8',
  textSecondary: '#94A3B8',
  textTertiary: '#64748B',
  textQuaternary: '#475569',
  cyan: '#00D4FF',
  cyanLight: '#7DE5F5',
  cyanSoft: 'rgba(0, 212, 255, 0.10)',
  cyanGlow: 'rgba(0, 212, 255, 0.35)',
  danger: '#FF4560',
};

const F = {
  ui: "'DM Sans', -apple-system, system-ui, sans-serif",
};

export default function Login() {
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();
  const { setUserContext } = useAdmin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Sign in | Meridian Registry';
    if (!document.querySelector('link[href*="DM+Sans"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/auth/login', { email, password });
      if (res.data.success) {
        sessionStorage.setItem('meridian_token', res.data.token);
        setUserContext(res.data.role, res.data.client_name || null);
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        setError('Invalid email or password');
      } else {
        setError('Unable to connect. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: C.bgBase,
      color: C.textPrimary,
      fontFamily: F.ui,
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden',
      WebkitFontSmoothing: 'antialiased',
    }}>
      {/* Ambient glow */}
      <div aria-hidden style={{
        position: 'fixed',
        top: -300, right: -400,
        width: 900, height: 900,
        background: 'radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 65%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Nav */}
      <MiniNav onLogoClick={() => navigate('/')} />

      {/* Centered card */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '32px 16px',
        position: 'relative', zIndex: 1,
      }}>
        <div style={{
          width: '100%', maxWidth: 440,
          padding: '40px 32px',
          background: 'rgba(15, 22, 40, 0.8)',
          border: `1px solid ${C.borderDefault}`,
          borderRadius: 16,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <img
              alt="Meridian"
              src="/Meridian_M_logo-transparent.png"
              style={{ height: 88 }}
            />
          </div>

          {/* SSO button */}
          <PrimaryBtn
            onClick={() => loginWithRedirect()}
            full
          >Sign in with SSO</PrimaryBtn>

          <Divider />

          {/* Email/password form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Email">
              <Input
                type="email" required autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </Field>

            <Field label="Password">
              <div style={{ position: 'relative' }}>
                <Input
                  type={showPw ? 'text' : 'password'}
                  required autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ paddingRight: 56 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  style={{
                    position: 'absolute', right: 10, top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent', border: 'none',
                    color: C.textTertiary, cursor: 'pointer',
                    fontSize: 12, fontFamily: F.ui, padding: 4,
                  }}>{showPw ? 'Hide' : 'Show'}</button>
              </div>
            </Field>

            <PrimaryBtn type="submit" disabled={loading} full>
              {loading ? 'Signing in…' : 'Sign in'}
            </PrimaryBtn>

            {error && (
              <div role="alert" style={{
                background: 'rgba(255, 69, 96, 0.08)',
                border: `1px solid rgba(255, 69, 96, 0.3)`,
                borderRadius: 8,
                padding: '10px 14px',
                color: C.danger, fontSize: 13,
                textAlign: 'center',
              }}>{error}</div>
            )}
          </form>

          <p style={{
            textAlign: 'center',
            color: C.textTertiary, fontSize: 13,
            marginTop: 24, margin: '24px 0 0',
          }}>
            Don't have access?{' '}
            <Link to="/request-demo" style={{
              color: C.cyanLight, textDecoration: 'none', fontWeight: 600,
            }}>Request a demo →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ Shared components ═══════════════ */

function MiniNav({ onLogoClick }: { onLogoClick: () => void }) {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'rgba(8, 12, 24, 0.72)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderBottom: `1px solid ${C.borderSubtle}`,
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        padding: '14px 32px',
        display: 'flex', alignItems: 'center',
      }}>
        <button
          onClick={onLogoClick}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 10, padding: 0,
          }}>
          <img alt="Meridian" src="/Meridian_M_logo-transparent.png" style={{ height: 28 }} />
          <span style={{
            fontSize: 14, fontWeight: 700,
            letterSpacing: '0.22em',
            color: C.textPrimary, textTransform: 'uppercase',
          }}>Meridian</span>
        </button>
      </div>
    </nav>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 11, fontWeight: 600,
        color: C.textTertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      onFocus={e => {
        e.currentTarget.style.borderColor = C.cyan;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${C.cyanSoft}`;
        props.onFocus?.(e);
      }}
      onBlur={e => {
        e.currentTarget.style.borderColor = C.borderDefault;
        e.currentTarget.style.boxShadow = 'none';
        props.onBlur?.(e);
      }}
      style={{
        width: '100%',
        background: C.bgInput,
        border: `1px solid ${C.borderDefault}`,
        borderRadius: 8,
        padding: '10px 14px',
        color: C.textPrimary,
        fontFamily: F.ui,
        fontSize: 14,
        outline: 'none',
        transition: 'all 150ms ease',
        boxSizing: 'border-box',
        ...props.style,
      }}
    />
  );
}

function Divider() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      margin: '18px 0',
    }}>
      <div style={{ flex: 1, height: 1, background: C.borderSubtle }} />
      <span style={{
        fontSize: 11, color: C.textQuaternary,
        fontWeight: 600, letterSpacing: '0.2em',
      }}>OR</span>
      <div style={{ flex: 1, height: 1, background: C.borderSubtle }} />
    </div>
  );
}

function PrimaryBtn({
  children, onClick, disabled, type = 'button', full,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  full?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={e => {
        if (disabled) return;
        e.currentTarget.style.background = C.cyanLight;
        e.currentTarget.style.borderColor = C.cyanLight;
      }}
      onMouseLeave={e => {
        if (disabled) return;
        e.currentTarget.style.background = C.cyan;
        e.currentTarget.style.borderColor = C.cyan;
      }}
      style={{
        fontFamily: F.ui,
        fontWeight: 600, fontSize: 14,
        padding: '11px 20px',
        borderRadius: 10,
        background: C.cyan,
        color: '#000',
        border: `1px solid ${C.cyan}`,
        boxShadow: `0 0 0 1px ${C.cyanGlow}, 0 8px 24px -8px ${C.cyanGlow}`,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 120ms ease',
        width: full ? '100%' : undefined,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}>
      {children}
    </button>
  );
}
